import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/* ── Types ── */

export type SuccessLabel = 'healthy' | 'needs_attention' | 'stalled';

export interface ProducerSuccessRow {
  id: string;
  name: string;
  slug: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  has_description: boolean;
  activity_count: number;
  public_activity_count: number;
  image_count: number;
  booking_count: number;
  label: SuccessLabel;
  issues: string[];
}

export type SuccessFilter =
  | 'all'
  | 'needs_attention'
  | 'healthy'
  | 'stalled'
  | 'no_activities'
  | 'no_images'
  | 'no_bookings';

export interface SuccessSummary {
  total: number;
  healthy: number;
  needsAttention: number;
  stalled: number;
  noActivities: number;
  noImages: number;
  noBookings: number;
}

/* ── Classification logic ── */

function classify(p: {
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  has_description: boolean;
  activity_count: number;
  public_activity_count: number;
  image_count: number;
  booking_count: number;
}): { label: SuccessLabel; issues: string[] } {
  const issues: string[] = [];
  const daysSinceCreation = (Date.now() - new Date(p.created_at).getTime()) / 86_400_000;
  const daysSinceUpdate = (Date.now() - new Date(p.updated_at).getTime()) / 86_400_000;

  if (p.activity_count === 0) issues.push('Inga aktiviteter');
  if (p.image_count === 0) issues.push('Inga bilder');
  if (!p.has_description) issues.push('Ingen beskrivning');
  if (p.public_activity_count > 0 && p.booking_count === 0) issues.push('Inga bokningar');
  if (p.status === 'draft' && daysSinceCreation > 14) issues.push('Utkast > 14 dagar');
  if (p.status === 'public' && p.activity_count === 0 && p.image_count === 0) issues.push('Tunn profil');

  // Stalled: draft for >30 days and no update in 14 days
  if (p.status === 'draft' && daysSinceCreation > 30 && daysSinceUpdate > 14) {
    return { label: 'stalled', issues };
  }

  // Healthy: public, has activities, images, and description
  if (
    p.status === 'public' &&
    p.activity_count > 0 &&
    p.image_count > 0 &&
    p.has_description
  ) {
    return { label: 'healthy', issues };
  }

  // Everything else needs attention
  return { label: issues.length > 0 ? 'needs_attention' : 'healthy', issues };
}

/* ── Hook ── */

export function useProducerSuccess() {
  return useQuery({
    queryKey: ['producer-success'],
    staleTime: 120_000,
    queryFn: async (): Promise<{ rows: ProducerSuccessRow[]; summary: SuccessSummary }> => {
      // Fetch producers
      const { data: producers, error: pErr } = await supabase
        .from('producers')
        .select('id, name, slug, status, created_at, updated_at, published_at, description')
        .order('created_at', { ascending: false })
        .limit(500);
      if (pErr) throw pErr;

      const producerIds = (producers || []).map(p => p.id);
      if (producerIds.length === 0) {
        return { rows: [], summary: { total: 0, healthy: 0, needsAttention: 0, stalled: 0, noActivities: 0, noImages: 0, noBookings: 0 } };
      }

      // Parallel queries for counts
      const [eventsRes, mediaRes, bookingsRes] = await Promise.all([
        supabase
          .from('producer_events')
          .select('id, producer_id, status')
          .in('producer_id', producerIds),
        supabase
          .from('media_assets')
          .select('id, owner_id')
          .eq('owner_type', 'producer')
          .in('owner_id', producerIds),
        supabase
          .from('event_bookings')
          .select('id, producer_id')
          .in('producer_id', producerIds)
          .neq('status', 'cancelled'),
      ]);

      // Build count maps
      const activityMap = new Map<string, { total: number; public: number }>();
      for (const ev of eventsRes.data || []) {
        const entry = activityMap.get(ev.producer_id) || { total: 0, public: 0 };
        entry.total++;
        if (ev.status === 'public') entry.public++;
        activityMap.set(ev.producer_id, entry);
      }

      const imageMap = new Map<string, number>();
      for (const m of mediaRes.data || []) {
        if (m.owner_id) imageMap.set(m.owner_id, (imageMap.get(m.owner_id) || 0) + 1);
      }

      const bookingMap = new Map<string, number>();
      for (const b of bookingsRes.data || []) {
        bookingMap.set(b.producer_id, (bookingMap.get(b.producer_id) || 0) + 1);
      }

      // Build rows
      const rows: ProducerSuccessRow[] = (producers || []).map(p => {
        const acts = activityMap.get(p.id) || { total: 0, public: 0 };
        const imgs = imageMap.get(p.id) || 0;
        const bkgs = bookingMap.get(p.id) || 0;
        const hasDsc = !!(p.description && p.description.trim().length > 10);

        const { label, issues } = classify({
          status: p.status,
          published_at: p.published_at,
          created_at: p.created_at,
          updated_at: p.updated_at,
          has_description: hasDsc,
          activity_count: acts.total,
          public_activity_count: acts.public,
          image_count: imgs,
          booking_count: bkgs,
        });

        return {
          id: p.id,
          name: p.name,
          slug: p.slug,
          status: p.status,
          created_at: p.created_at,
          updated_at: p.updated_at,
          published_at: p.published_at,
          has_description: hasDsc,
          activity_count: acts.total,
          public_activity_count: acts.public,
          image_count: imgs,
          booking_count: bkgs,
          label,
          issues,
        };
      });

      // Sort: stalled first, then needs_attention, then healthy
      const order: Record<SuccessLabel, number> = { stalled: 0, needs_attention: 1, healthy: 2 };
      rows.sort((a, b) => order[a.label] - order[b.label]);

      const summary: SuccessSummary = {
        total: rows.length,
        healthy: rows.filter(r => r.label === 'healthy').length,
        needsAttention: rows.filter(r => r.label === 'needs_attention').length,
        stalled: rows.filter(r => r.label === 'stalled').length,
        noActivities: rows.filter(r => r.activity_count === 0).length,
        noImages: rows.filter(r => r.image_count === 0).length,
        noBookings: rows.filter(r => r.public_activity_count > 0 && r.booking_count === 0).length,
      };

      return { rows, summary };
    },
  });
}
